/**
 * Client-specific tracking adaptations.
 */
window.WSHOP_TRACKER = window.WSHOP_TRACKER || {};

const allowedSet = new Set([
    "context",
    "order",
    "landing_page",
    "login",
    "sign_up",
    "newsletter_submit",
    "optout_newsletter",
    "compte_invite_inscription_etape_connexion",
    "tunnel_etape_connexion_invite",
    "social_connect",
    "search",
    "stock_alert",
    "filter_interaction",
    "store_locator",
    "store_availability",
    "delivery_type_seen",
    "delivery_type_click",
    "view_item_list",
    "select_item",
    "view_item",
    "add_to_wishlist",
    "add_to_cart",
    "view_cart",
    "remove_from_cart",
    "begin_checkout",
    "add_shipping_info",
    "add_payment_info",
    "purchase",
    "purchase-with-sessionstorage-object",
    "purchase-without-sessionstorage-object",
    "share_cart"
]);

function renameRefSizeColorKey(value) {
    if (Array.isArray(value)) {
        return value.map(renameRefSizeColorKey);
    }

    if (value && typeof value === "object") {
        return Object.entries(value).reduce((acc, [key, nestedValue]) => {
            const nextKey = key === "refSizeColor" ? "item_ref_size_color" : key;
            acc[nextKey] = renameRefSizeColorKey(nestedValue);
            return acc;
        }, {});
    }

    return value;
}

function removeWshopProductIdFromItems(payload) {
    if (Array.isArray(payload)) {
        return payload.map(removeWshopProductIdFromItems);
    }

    if (!isObject(payload)) {
        return payload;
    }

    return Object.entries(payload).reduce((nextPayload, [key, value]) => {
        if (key === "wshop_product_id") {
            return nextPayload;
        }

        nextPayload[key] = removeWshopProductIdFromItems(value);
        return nextPayload;
    }, {});
}

function removeCurrencyFromEcommerceItems(payload) {
    if (!isObject(payload)) {
        return payload;
    }

    const ecommerce = payload.ecommerce;

    if (!isObject(ecommerce) || !Array.isArray(ecommerce.items)) {
        return payload;
    }

    return {
        ...payload,
        ecommerce: {
            ...ecommerce,
            items: ecommerce.items.map((item) => {
                if (!isObject(item)) {
                    return item;
                }

                const { currency, ...nextItem } = item;
                return nextItem;
            })
        }
    };
}

function adaptContextPayloadToClientSchema(payload) {
    const context = isObject(payload?.context) ? payload.context : {};
    const user = isObject(payload?.user) ? payload.user : {};
    const card = isObject(user?.card) ? user.card : {};

    const rawCategory = context?.page?.category ?? "other";

    let pageType = rawCategory;
    if (rawCategory === "category") {
        pageType = "plp";
    } else if (rawCategory === "product") {
        pageType = "pdp";
    }

    const isConnected = Object.keys(user).length > 0;
    const loginStatus = isConnected
        ? 1
        : 0;

    return {
        page_type: pageType,
        page_path: window.location.pathname + window.location.search,
        page_category: rawCategory,
        page_referrer: document.referrer ?? "",
        page_id: context?.page?.id ?? "",
        page_cmpid: context?.cmpid ?? "",
        user_id: user?.user_id ?? "",
        user_zipcode_sha256: user?.user_zipcode_sha256 ?? "",
        user_device: context?.navigation?.device ?? "",
        country_code: context?.country_version ?? "",
        language_code: context?.lang ?? "",
        login_status: loginStatus,
        email_hashed: user?.user_email_sha256 ?? "",
        subscription_loyalty: card?.validity ?? "",
        user_loyalty_enrollment_date: card?.creationDate ?? "",
        loyalty_status: card?.status ?? ""
    };
}

const payloadNormalizers = {
    context: (payload) => adaptContextPayloadToClientSchema(payload),
    order: (payload) => adaptContextPayloadToClientSchema(payload),
    view_cart: (payload) => removeViewCartCheckoutStepFields(payload),
    newsletter_submit: (payload) => removeNewsletterSubmitStepFields(payload),
    login: (payload) => removeLoginEmailField(payload),
    filter_interaction: (payload) => removeTrailingFilterSeparator(payload),
    select_item: (payload) => adaptSelectItemPayload(payload),
    purchase: (payload) => adaptPurchasePayload(payload),
    store_locator: (payload) => adaptStoreLocatorPayload(payload),
    default: (payload) => payload
};

function isAllowedEvent(eventName) {
    return allowedSet.has(eventName);
}

function normalizePayload(event) {
    const normalize = payloadNormalizers[event.name] || payloadNormalizers.default;

    return normalize(event.payload, event);
}

function transformPayload(payload) {
    return removeCurrencyFromEcommerceItems(
        renameRefSizeColorKey(removeWshopProductIdFromItems(payload))
    );
}

function isObject(value) {
    return value && typeof value === "object" && !Array.isArray(value);
}

function removeViewCartCheckoutStepFields(payload) {
    if (!isObject(payload)) return payload;

    const { checkout_step_name, checkout_step_number, ...nextPayload } = payload;

    return nextPayload;
}

function removeNewsletterSubmitStepFields(payload) {
    if (!isObject(payload)) return payload;

    const { form_location, ...nextPayload } = payload;

    return nextPayload;
}

// The tracking plan does not expect user_email, and it must not be exposed in clear text.
function removeLoginEmailField(payload) {
    if (!isObject(payload)) return payload;

    const { user_email, ...nextPayload } = payload;

    return nextPayload;
}

// The core joins the filters with " | " and leaves a separator after the last one ("a_1 | b_2 |")
function removeTrailingFilterSeparator(payload) {
    if (!isObject(payload) || typeof payload.filters_applied !== "string") return payload;

    return {
        ...payload,
        filters_applied: payload.filters_applied.replace(/\s*\|\s*$/, "")
    };
}

function adaptSelectItemPayload(payload) {
    if (!isObject(payload) || !isObject(payload.ecommerce)) return payload;

    const selectedItems = Array.isArray(payload.ecommerce.items) ? payload.ecommerce.items : [];

    return {
        ...payload,
        ecommerce: {
            ...payload.ecommerce,
            currency: window.siteCurrency?.code ?? "",
            value: selectedItems[0]?.price ?? ""
        }
    };
}

function adaptStoreLocatorPayload(payload) {
    if (!isObject(payload)) return payload;

    return {
        event: 'store_locator',
        store_name: payload.store_name ?? payload.storeLocator_name ?? "",
        store_country: payload.store_country ?? payload.storeLocator_country ?? "",
        store_city: payload.store_city ?? payload.storeLocator_town.city ?? ""
    };
}

// In the checkout tunnel, the core login forms send sign_up even when the customer orders as a guest.
// The tunnel is recognised by the hidden clientPwd field (a generated password), and an account is only
// created when the customer typed a password in passbdd: otherwise the email sign_up is dropped
// (social sign-ups from the same page are kept).
function isGuestCheckoutSignUp() {
    if (typeof document === "undefined") return false;

    const isCheckoutTunnel = document.querySelector('input[type="hidden"][name="clientPwd"]') !== null;
    if (!isCheckoutTunnel) return false;

    const hasTypedPassword = Array.from(document.querySelectorAll('input[name="passbdd"]'))
        .some((input) => input.value !== "");

    return !hasTypedPassword;
}

const clientConfig = {
  eventProcess(event) {
    const normalizedEventName = event.name === "storeLocator" ? "store_locator" : event.name;

    if (!isAllowedEvent(normalizedEventName)) return false;

    if (normalizedEventName === "sign_up" && event.payload?.method === "email" && isGuestCheckoutSignUp()) return false;

    const normalized = normalizePayload({ ...event, name: normalizedEventName });
    const payload = transformPayload(normalized);

    return { ...event, name: normalizedEventName, payload };
  }
};

if (typeof WSHOP_TRACKER.setClientConfig === "function") {
    WSHOP_TRACKER.setClientConfig(clientConfig);
} else {
    WSHOP_TRACKER.clientConfig = {
        ...(WSHOP_TRACKER.clientConfig || {}),
        ...clientConfig
    };
}

/**
 * Adapts the purchase payload to set the transaction_id to the order_number.
 * @param {Object} payload - The raw event payload.
 * @returns {Object} The adapted payload with updated transaction_id.
 */
function adaptPurchasePayload(payload) {
    if (!isObject(payload)) return payload;

    const ecommerce = payload.ecommerce;

    if (!isObject(ecommerce)) return payload;

    const nextEcommerce = { ...ecommerce };

    // Check if the nested ecommerce object has an order_number
    if (ecommerce.order_number !== undefined) {
        nextEcommerce.transaction_id = ecommerce.order_number;
    }

    if (isCardPaymentLabel(ecommerce.payment_type)) {
        nextEcommerce.payment_type = "CB";
    }

    return {
        ...payload,
        ecommerce: nextEcommerce
    };
}

// purchase sends the label of the card payment type ("Carte Bancaire", not translated in the database),
// while add_payment_info sends "CB": the tracking plan expects "CB" on both events.
function isCardPaymentLabel(label) {
    return typeof label === "string" && label.trim().toLowerCase() === "carte bancaire";
}
